import React from 'react';
import Markdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import { remarkBrOnly } from '../../utils/remark/remarkBrOnly';
import { remarkMathToHast } from '../../utils/remark/remarkMathToHast';
import {
	preprocessLaTeX,
	shieldLatexForRemark,
	trimIncompleteLatex,
	closeIncompleteLatex,
} from '../../utils/latexProcessing';

interface MarkdownRendererProps {
	/** Raw markdown / LLM output to render. */
	content: string;
	/** Custom react-markdown Components. */
	components: Components;
	/** Extra className applied to the root wrapper created by react-markdown. */
	className?: string;
	/** When true, trailing incomplete LaTeX is trimmed rather than auto-closed. */
	isStreaming?: boolean;
	/**
	 * When true, caller has already run trim/close on `content` — skip the
	 * internal scan. Used by streaming callers that need the trimmed result
	 * for sibling logic (e.g. AssistantMessage's `mathIsStreaming` flag).
	 */
	preProcessed?: boolean;
}

/**
 * Thin wrapper around `<Markdown>` that encapsulates the full plugin pipeline
 * (remarkMath → remarkGfm → remarkBrOnly → remarkMathToHast, rehypeRaw) and
 * the LaTeX pre-processing chain.
 */
export const MarkdownRenderer = React.memo(function MarkdownRenderer({
	content,
	components,
	className = 'text-md text-neutral-100 [&>:last-child]:mb-0',
	isStreaming = false,
	preProcessed = false,
}: MarkdownRendererProps) {
	let processed: string;
	if (preProcessed) {
		processed = content;
	} else if (isStreaming) {
		processed = trimIncompleteLatex(content);
	} else {
		processed = closeIncompleteLatex(content);
	}

	return (
		<Markdown
			className={className}
			remarkPlugins={[remarkMath, remarkGfm, remarkBrOnly, remarkMathToHast]}
			rehypePlugins={[rehypeRaw]}
			components={components}
			skipHtml={false}
			allowedElements={undefined}
			disallowedElements={['script', 'iframe', 'object', 'embed']}
		>
			{shieldLatexForRemark(preprocessLaTeX(processed))}
		</Markdown>
	);
});
